import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useRef, useState } from "react";
import {
  ArrowPathIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  UserMinusIcon,
  WalletIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useSnapmintRemoveMembershipAPI } from "@/app/hooks/api_hooks/Cart/useSnapmintRemoveMembershipAPI";
import { ICartUserInvoiceListResponseListData } from "@/api_framework/api_modals/Cart";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  invoice: ICartUserInvoiceListResponseListData;
  /** Called once the memberships are removed, to reload the regenerated invoice. */
  onRemoved: () => void;
};

const formatAmount = (amount?: number | string | null) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const OUTCOMES = [
  {
    icon: UserMinusIcon,
    title: "Memberships removed",
    detail: "Except any already used on another invoice.",
  },
  {
    icon: DocumentTextIcon,
    title: "Benefits recalculated",
    detail: "Other active memberships apply, else full price.",
  },
  {
    icon: WalletIcon,
    title: "Invoice regenerated",
    detail: "Any amount paid goes to the wallet.",
  },
];

// Confirmation for removing every membership on an invoice whose Snapmint payment did not go through.
const RemoveMembershipModal: React.FC<Props> = ({ open, setOpen, invoice, onRemoved }) => {
  const cancelButtonRef = useRef(null);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const { execute: removeMembership } = useSnapmintRemoveMembershipAPI();

  const snapmintAmount = (invoice?.snapmint_meta_data?.pending_orders ?? []).reduce(
    (total, order) => total + Number(order?.order_value || 0),
    0,
  );

  const handleClose = () => {
    if (isRemoving) return;
    setIsAcknowledged(false);
    setOpen(false);
  };

  const removeMemberships = async () => {
    setIsRemoving(true);
    const { status } = await removeMembership(invoice?.invoice_id);
    setIsRemoving(false);
    if (!status) return;
    setIsAcknowledged(false);
    setOpen(false);
    onRemoved();
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={handleClose}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block w-full align-bottom bg-white rounded-2xl text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-lg">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 pt-6">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 ring-4 ring-red-50/50">
                      <UserMinusIcon className="h-5 w-5 text-[#F72F03]" />
                    </span>
                    <div>
                      <Dialog.Title className="text-base font-semibold text-[#1F1F23]">
                        Remove all memberships?
                      </Dialog.Title>
                      <p className="text-[#808DA0] text-xs mt-1 leading-relaxed">
                        Snapmint EMI not completed. This can't be undone.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isRemoving}
                    className="text-gray-400 hover:text-gray-600 transition-colors disabled:cursor-not-allowed"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-4">
                  {/* Invoice summary */}
                  <div className="grid grid-cols-3 divide-x divide-gray-100 rounded-xl border border-gray-100 bg-[#F8FAFC]">
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-[#808DA0]">Invoice</p>
                      <p className="mt-0.5 truncate text-xs font-semibold text-[#1F1F23]" title={invoice?.invoice_number}>
                        {invoice?.invoice_number || "-"}
                      </p>
                    </div>
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-[#808DA0]">Invoice total</p>
                      <p className="mt-0.5 text-xs font-semibold text-[#1F1F23]">
                        {formatAmount(invoice?.invoice_total)}
                      </p>
                    </div>
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-[#808DA0]">Snapmint EMI</p>
                      <p className="mt-0.5 text-xs font-semibold text-[#F72F03]">{formatAmount(snapmintAmount)}</p>
                    </div>
                  </div>

                  {/* What happens */}
                  <div>
                    <p className="text-xs font-semibold text-[#1F1F23] mb-2">What happens next</p>
                    <ul className="space-y-2.5">
                      {OUTCOMES.map(({ icon: Icon, title, detail }) => (
                        <li key={title} className="flex items-start gap-2.5">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#EEF2F8]">
                            <Icon className="h-3.5 w-3.5 text-[#193A6A]" />
                          </span>
                          <div>
                            <p className="text-xs font-medium text-[#1F1F23]">{title}</p>
                            <p className="text-[11px] leading-relaxed text-[#808DA0]">{detail}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-[#808DA0]">
                    <InformationCircleIcon className="h-4 w-4 shrink-0 text-[#808DA0]" />
                    Blocked if any Snapmint payment is settled.
                  </p>

                  {/* Acknowledgement */}
                  <label
                    className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors ${
                      isAcknowledged ? "border-[#F72F03]/40 bg-red-50/60" : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isAcknowledged}
                      disabled={isRemoving}
                      onChange={(e) => setIsAcknowledged(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#F72F03] focus:ring-[#F72F03]"
                    />
                    <span className="text-xs font-medium leading-relaxed text-[#1F1F23]">
                      I understand memberships and their benefits will be removed.
                    </span>
                  </label>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    type="button"
                    ref={cancelButtonRef}
                    onClick={handleClose}
                    disabled={isRemoving}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-[#1F1F23] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    No, Go Back
                  </button>
                  <button
                    type="button"
                    disabled={!isAcknowledged || isRemoving}
                    onClick={removeMemberships}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#F72F03] px-4 py-2 text-sm font-medium text-white hover:bg-[#d92802] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRemoving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                    {isRemoving ? "Removing..." : "Yes, Remove Now"}
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default RemoveMembershipModal;
